import type { Ref } from 'react'
import { useState, useMemo } from 'react'
import { useQuery } from '@truepill/tpos-react-router'
import type { ResolveTriageParams } from '@truepill/tpos-types'
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ButtonsContainer, InputContainer, ModalHeader, ModalWrapper } from 'components/Modal'
import { CancelButton } from 'components/PageStructure'
import { StyledSelect } from 'components/RXTable'
import { SEARCH_ORDER_TRIAGE_REASONS } from 'gql'
import useAutoFocus from 'hooks/useAutoFocus'
import useSelectedLocations from 'hooks/useSelectedLocations'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import styled from 'styled-components'
import { bodyPrimaryColor } from 'styles/styleVariables'
import { TriageReasonMap } from 'utils'

type BulkResolveTriageModalProps = {
  onConfirm: (params: ResolveTriageParams) => void
  initialParams?: ResolveTriageParams
}

const BulkResolveTriageModal = ({ onConfirm, initialParams }: BulkResolveTriageModalProps): JSX.Element => {
  const { dismissModal } = useModalContext()
  const autoFocusRef = useAutoFocus()
  const { selectedLocationIds } = useSelectedLocations()
  const [triageReason, setTriageReason] = useState<string>(initialParams?.reason ?? '')

  const { data, loading } = useQuery(SEARCH_ORDER_TRIAGE_REASONS, {
    variables: {
      selectedLocationIds,
    },
  })

  const reasonOptions = useMemo(() => {
    const reasons: string[] = (data?.searchOrderTriageReasons || []).map((r: { _id: string }) => r._id)
    return reasons.map(r => TriageReasonMap[r] || r)
  }, [data?.searchOrderTriageReasons])

  const selectedValue = triageReason ? TriageReasonMap[triageReason] || triageReason : undefined

  const handleConfirm = () => {
    if (!triageReason) return
    onConfirm({ reason: triageReason })
    dismissModal()
  }

  return (
    <ModalWrapper id="BulkResolveTriageModal">
      <ModalHeader>
        <IconWrapper>
          <DangerIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Resolve Triage</h2>
      </ModalHeader>
      <InputContainer>
        <ReasonSelect
          data-testid="bulk-resolve-triage-reason"
          ref={autoFocusRef as Ref<HTMLButtonElement>}
          multiple={false}
          maximumHeight="12rem"
          modal={true}
          value={selectedValue}
          placeholder={loading ? 'Loading reasons...' : 'Select a triage reason to resolve...'}
          options={reasonOptions}
          disabled={loading}
          onChange={([option]: { value: string }[]) => {
            if (!option) return
            // Reverse-map display label back to reason ID
            const allReasons: string[] = (data?.searchOrderTriageReasons || []).map((r: { _id: string }) => r._id)
            const reasonId = allReasons.find(r => (TriageReasonMap[r] || r) === option.value)
            if (reasonId) setTriageReason(reasonId)
          }}
        />
      </InputContainer>
      <ButtonsContainer>
        <CancelButton label="Cancel" onClick={dismissModal} />
        <SaveButton disabled={!triageReason} label="Confirm" isModal onClick={handleConfirm} />
      </ButtonsContainer>
    </ModalWrapper>
  )
}

const ReasonSelect = styled(StyledSelect)`
  height: 2.5rem;
`

export default BulkResolveTriageModal
